import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface DayCalorie {
  day: string;
  intake: number;
  burned: number;
}

interface CalorieComparisonProps {
  data?: DayCalorie[];
  vsLastWeekPercent?: number;
  bare?: boolean;
}

const formatK = (v: number) => `${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`;

const BarLabel = ({ x, y, width, height, value }: any) => {
  if (!value || height < 20) return null;
  const cx = x + width / 2;
  const cy = y + 20;
  return (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      dominantBaseline="middle"
      fill="#010101"
      fontSize={9}
      fontFamily="Inter, sans-serif"
      fontWeight={600}
      transform={`rotate(-90, ${cx}, ${cy})`}
    >
      {formatK(value)}
    </text>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[6px] px-3 py-2 shadow text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1">{String(label).slice(0, 2)}</p>
      {payload.map((p: any) => {
        const color = p.name === "burned" ? "#D9B800" : p.color;
        return (
          <div key={p.name} className="flex items-center gap-1.5 mt-0.5">
            <span
              className="w-2 h-2 rounded-full inline-block flex-shrink-0"
              style={{ background: color }}
            />
            <span style={{ color }} className="font-medium">
              {p.name === "intake" ? "Calorie intake" : "Calorie burned"} · {p.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Computes a clean Y-axis domain and tick count based on the data range.
 * - min is always 0
 * - max is rounded up to the nearest "nice" step
 * - returns ~5 evenly spaced ticks
 */
function getDynamicYAxis(data: DayCalorie[]) {
  const allValues = data.flatMap((d) => [d.intake, d.burned]).filter((v) => v > 0);
  const maxValue = allValues.length ? Math.max(...allValues) : 2000;

  const rawStep = maxValue / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  // Minimum step of 100 ensures no two ticks share the same formatK label (0.1k resolution)
  const niceStep = Math.max(100, Math.ceil(rawStep / magnitude) * magnitude);
  const niceMax = niceStep * Math.ceil(maxValue / niceStep);
  const tickCount = Math.round(niceMax / niceStep) + 1;
  const ticks = Array.from({ length: tickCount }, (_, i) => i * niceStep);

  return { domain: [0, niceMax] as [number, number], ticks };
}

const CalorieComparison: React.FC<CalorieComparisonProps> = ({
  data = [],
  vsLastWeekPercent,
  bare = false,
}) => {
  const isUp = (vsLastWeekPercent ?? 0) >= 0;
  const { domain, ticks } = getDynamicYAxis(data);

  const chart = (
    <ResponsiveContainer width="100%" height={144}>
      <BarChart
        data={data}
        barCategoryGap="15%"
        barGap={3}
        margin={{ top: 20, right: 4, left: -20, bottom: 0 }}
      >
        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
        <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
        <XAxis
          dataKey="day"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          dy={6}
          tickFormatter={(v) => String(v).slice(0, 2)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
          tickFormatter={formatK}
          domain={domain}
          ticks={ticks}
        />
        <Bar
          dataKey="intake"
          name="intake"
          fill="#FF9130"
          radius={[3, 3, 0, 0]}
          maxBarSize={40}
          minPointSize={2}
        />
        <Bar
          dataKey="burned"
          name="burned"
          fill="#FFE448"
          radius={[3, 3, 0, 0]}
          maxBarSize={40}
          minPointSize={2}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  if (bare) return chart;

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Calorie Comparison
        </span>
        {vsLastWeekPercent != null && (
          <div className="flex items-center gap-2">
            <span
              className={`text-[10px] 3xl:text-[12px] font-semibold px-2 py-0.5 rounded-[6px] border font-inter ${
                isUp
                  ? "text-[#0DB5B5] border-[#0DB5B5] bg-[#F0FAFA]"
                  : "text-[#FF4D4D] border-[#FF4D4D] bg-[#FFF0F0]"
              }`}
            >
              {isUp ? "+" : ""}{vsLastWeekPercent}% consumed
            </span>
            <span className="text-[10px] 3xl:text-[12px] text-[#8E8E93] font-inter">vs last week</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="flex-1">{chart}</div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-1">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF9130] inline-block" />
          <span className="text-[12px] 3xl:text-[14px] text-[#1F1F1F] font-inter">Calorie intake</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FFE448] inline-block" />
          <span className="text-[12px] 3xl:text-[14px] text-[#1F1F1F] font-inter">Calorie burned</span>
        </div>
      </div>
    </div>
  );
};

export default CalorieComparison;